/**
 * 上传页图片预览（EDG-561 SC-06 / SC-08，PRD S3.2）：
 * 缩略图与大图都用**本地压缩预览**，不加载原图。
 *
 * 返回 blob URL；调用方负责在移除 / 卸载时 `URL.revokeObjectURL`。
 * 任何一步失败（无 canvas、解码失败、toBlob 返回 null）都回退为 `URL.createObjectURL(file)`，
 * 保证有图可显示；jsdom 等无 canvas 环境同样走回退。
 */
export const PREVIEW_MAX_EDGE = 1600;
export const PREVIEW_QUALITY = 0.8;

export const isImageFile = (file: { name: string; type?: string }): boolean => {
  if (file.type?.startsWith("image/")) {
    return true;
  }
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return ["png", "jpg", "jpeg", "webp"].includes(ext);
};

const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image decode failed"));
    img.src = url;
  });

export async function createImagePreview(
  file: File,
  maxEdge: number = PREVIEW_MAX_EDGE,
): Promise<string> {
  const original = URL.createObjectURL(file);
  try {
    if (typeof document === "undefined") {
      return original;
    }
    const img = await loadImage(original);
    const longest = Math.max(img.naturalWidth, img.naturalHeight);
    if (!longest || longest <= maxEdge) {
      return original; // 本来就不大，无需再压
    }
    const scale = maxEdge / longest;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return original;
    }
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", PREVIEW_QUALITY),
    );
    if (!blob) {
      return original;
    }
    URL.revokeObjectURL(original);
    return URL.createObjectURL(blob);
  } catch {
    return original;
  }
}
